import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { webApi } from '../services/api';
import { TransactionDetailSheet } from '../components/TransactionDetailSheet';
import { TransactionListRow } from '../components/TransactionListItems';
import { formatInr } from '../utils/transactionDisplay';
import { isCanceledRequest } from '../utils/abortRequest';

const PERIODS = [
  { key: 'today', tabLabel: 'Today', amountLabel: "Today's Amount", countLabel: "Today's Tips" },
  { key: 'week', tabLabel: 'Weekly', amountLabel: 'Weekly Amount', countLabel: 'Weekly Tips' },
  { key: 'month', tabLabel: 'Monthly', amountLabel: 'Monthly Amount', countLabel: 'Monthly Tips' }
];

const REPORT_LIMIT = 200;

function TransactionsAllPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('today');
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailTx, setDetailTx] = useState(null);

  const periodMeta = PERIODS.find((p) => p.key === period) || PERIODS[0];

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setError('');
    (async () => {
      try {
        const data = await webApi.getTransactionReport(
          { period, limit: REPORT_LIMIT, offset: 0 },
          { signal: ac.signal }
        );
        if (ac.signal.aborted) return;
        setTotalAmount(Number(data?.totalAmount ?? 0));
        setTotalCount(Number(data?.totalCount ?? 0));
        setTransactions(Array.isArray(data?.transactions) ? data.transactions : []);
      } catch (err) {
        if (ac.signal.aborted || isCanceledRequest(err)) return;
        setError(err.message || 'Failed to load tips');
        setTransactions([]);
        setTotalAmount(0);
        setTotalCount(0);
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [period]);

  return (
    <div className="tips-all-page">
      <div className="tips-all-fixed-head">
        <header className="tips-all-topbar">
          <button type="button" className="tips-all-back" onClick={() => navigate(-1)} aria-label="Go back">
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
          </button>
          <h1 className="tips-all-topbar-title">Tips</h1>
          <span className="tips-all-topbar-spacer" aria-hidden="true" />
        </header>

        <div className="tips-all-fixed-sub">
          <div className="tips-all-metrics">
            <div className="tips-all-metric-card">
              <p className="tips-all-metric-label">{periodMeta.amountLabel}</p>
              <p className="tips-all-metric-value">{loading ? '…' : formatInr(totalAmount)}</p>
            </div>
            <div className="tips-all-metric-card">
              <p className="tips-all-metric-label">{periodMeta.countLabel}</p>
              <p className="tips-all-metric-value tips-all-metric-value--count">
                {loading ? '…' : totalCount.toLocaleString('en-IN')}
              </p>
            </div>
          </div>

          <div className="tips-all-tabs" role="tablist" aria-label="Time period">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                role="tab"
                aria-selected={period === p.key}
                className={`tips-all-tab${period === p.key ? ' tips-all-tab--active' : ''}`}
                onClick={() => setPeriod(p.key)}
              >
                {p.tabLabel}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="tips-all-body">
        {error ? (
          <p className="tips-all-error" role="alert">
            {error}
          </p>
        ) : null}

        {loading ? (
          <p className="tips-all-loading">Loading…</p>
        ) : (
          <div className="tips-all-list">
            {transactions.length === 0 ? (
              <div className="tips-all-empty">No tips in this period.</div>
            ) : (
              transactions.map((tx) => (
                <TransactionListRow key={tx.id} transaction={tx} onViewDetails={setDetailTx} />
              ))
            )}
          </div>
        )}
      </div>

      {detailTx ? <TransactionDetailSheet transaction={detailTx} onClose={() => setDetailTx(null)} /> : null}
    </div>
  );
}

export default TransactionsAllPage;
